import type { WorldScore } from '@am/protocol';
import { api } from './account';

/*
 * The players' world on the game server: leaderboards, public profiles with achievements and
 * titles, open co-op rooms and who is online. Read-only views; the server decides achievements
 * and the world score from uploaded saves (see packages/protocol/src/achievements.ts and
 * ranking.ts).
 */

/** The world leaderboards: by the world score, the most valuable airport, the best rating. */
export type Board = 'world' | 'value' | 'rating';

export interface BoardRow {
  rank: number;
  username: string;
  title: string;
  airportName: string;
  score: number;
}

export interface Leaderboard {
  by: Board;
  players: number;
  entries: BoardRow[];
  me: BoardRow | null;
  /** Rows just above and below the player when they are further down than `entries`. */
  around?: BoardRow[];
}

export interface BestAirport {
  value: number;
  airportName: string;
  day: number;
  rating: number;
  departures: number;
  at: number;
}

export interface PublicProfile {
  username: string;
  joined: number;
  title: string;
  rank: string;
  points: number;
  /** Achievement id → when unlocked. */
  achievements: Record<string, number>;
  /** Titles the player may pick (their own profile only matters). */
  titles: string[];
  best: BestAirport | null;
  bestRating: number;
  mostDepartures: number;
  saves: number;
  coopSaves: number;
  lastSeen: number;
  total: number;
  /** The world score and its parts, the place on the world board (null: not on it) and how many
   * are on it. */
  world?: WorldScore;
  worldRank?: number | null;
  worldPlayers?: number;
  /** Own profile only. */
  email?: string;
}

export interface OpenRoom {
  code: string;
  airportName: string;
  host: string;
  title: string;
  players: number;
  max: number;
  createdAt: number;
}

export interface ServerStats {
  accounts: number;
  online: number;
  rooms: number;
}

export function leaderboard(by: Board): Promise<Leaderboard> {
  return api<Leaderboard>('GET', `/leaderboard?by=${by}`);
}

export function myProfile(): Promise<PublicProfile> {
  return api<PublicProfile>('GET', '/profile');
}

export function pickTitle(title: string | null): Promise<PublicProfile> {
  return api<PublicProfile>('PUT', '/profile', { title });
}

export async function profileOf(username: string): Promise<PublicProfile> {
  const profile = await api<PublicProfile>('GET', `/profiles/${encodeURIComponent(username)}`);
  titleCache.set(username.toLowerCase(), { title: profile.title, at: Date.now() });
  return profile;
}

export async function openRooms(): Promise<OpenRoom[]> {
  return (await api<{ rooms: OpenRoom[] }>('GET', '/rooms')).rooms;
}

/** Lists a co-op room the player hosts for everyone to join. */
export function listRoom(code: string, airportName: string): Promise<unknown> {
  return api('POST', '/rooms', { code, airportName });
}

export function unlistRoom(code: string): Promise<unknown> {
  return api('DELETE', `/rooms/${encodeURIComponent(code)}`);
}

export function serverStats(): Promise<ServerStats> {
  return api<ServerStats>('GET', '/stats');
}

const TITLE_TTL_MS = 5 * 60_000;
const titleCache = new Map<string, { title: string | null; at: number }>();
const pending = new Map<string, Promise<string | null>>();

/** A player's shown title (cached a few minutes); null for names without an account. */
export function titleOf(username: string): Promise<string | null> {
  const key = username.toLowerCase();
  const cached = titleCache.get(key);
  if (cached && Date.now() - cached.at < TITLE_TTL_MS) return Promise.resolve(cached.title);
  let request = pending.get(key);
  if (!request) {
    request = profileOf(username)
      .then((p) => p.title)
      .catch(() => {
        titleCache.set(key, { title: null, at: Date.now() });
        return null;
      })
      .finally(() => pending.delete(key));
    pending.set(key, request);
  }
  return request;
}

/** The cached title, if known, without asking the server. */
export function cachedTitle(username: string): string | null {
  return titleCache.get(username.toLowerCase())?.title ?? null;
}
